import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaTimes, FaLanguage } from 'react-icons/fa';
import FilterPanel from '../filter_Panel/Fillter_Panel';
import { fetchTranslateLogs } from '../../services/service';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';

const TranslateLogList = ({ onSelect, filters, onFilterChange, onClearFilters }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  const auth = useAsgardeoAuth();

  // Helper function to highlight search text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-black px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  // Helper function to get display text with highlighting
  const getDisplayTextWithHighlight = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    
    const displayText = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    
    if (filters.search && filters.search.trim() !== '') {
      return highlightText(displayText, filters.search);
    }
    
    return displayText;
  };

  const loadLogs = async (page = 1, reset = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: filters.limit || 100,
        authContext: auth
      };

      // Add date filters if they exist
      if (filters.fromDate) params.from = filters.fromDate;
      if (filters.toDate) params.to = filters.toDate;
      
      // Add status filter if specified
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      const response = await fetchTranslateLogs(params);
      
      if (reset) {
        setLogs(response.logs || []);
        console.log('Reset logs:', response.logs?.length || 0);
      } else {
        setLogs(prev => {
          const newLogs = [...prev, ...(response.logs || [])];
          console.log('Appended logs:', newLogs.length);
          return newLogs;
        });
      }
      
      setTotalLogs(response.total || 0);
      // Check if there are more logs to load
      const receivedLogs = response.logs || [];
      const currentLimit = filters.limit || 100;
      const totalCount = response.total || 0;
      const currentCount = reset ? receivedLogs.length : logs.length + receivedLogs.length;
      
      // Check if there are more logs to load based on total count and current count
      const hasMoreLogs = currentCount < totalCount && receivedLogs.length === currentLimit;
      setHasMore(hasMoreLogs);
      setCurrentPage(page);
      
      // Debug logging
      console.log('LoadLogs Debug:', {
        receivedLogs: receivedLogs.length,
        currentLimit,
        totalCount,
        currentCount,
        hasMore: hasMoreLogs,
        page,
        reset
      });
    } catch (err) {
      console.error('Error loading translate logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(1, true);
  }, [filters.fromDate, filters.toDate, filters.status, filters.limit]);

  const handleLogClick = (log) => {
    setSelectedLogId(log._id || log.id);
    onSelect(log);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadLogs(currentPage + 1, false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!filters.search || filters.search.trim() === '') return true;
    
    const searchLower = filters.search.toLowerCase();
    return (
      (log.input_text && log.input_text.toLowerCase().includes(searchLower)) ||
      (log.translated_text && log.translated_text.toLowerCase().includes(searchLower)) ||
      (log.source_to_target && log.source_to_target.toLowerCase().includes(searchLower)) ||
      (log.source_type && log.source_type.toLowerCase().includes(searchLower)) ||
      // Keep backward compatibility with old field names
      (log.originalText && log.originalText.toLowerCase().includes(searchLower)) ||
      (log.translatedText && log.translatedText.toLowerCase().includes(searchLower)) ||
      (log.sourceLanguage && log.sourceLanguage.toLowerCase().includes(searchLower)) ||
      (log.targetLanguage && log.targetLanguage.toLowerCase().includes(searchLower)) ||
      (log.source_language && log.source_language.toLowerCase().includes(searchLower)) ||
      (log.target_language && log.target_language.toLowerCase().includes(searchLower))
    );
  });

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Helper function to get filter summary
  const getFilterSummary = () => {
    const activeFilters = [];
    
    if (filters.search && filters.search.trim() !== '') activeFilters.push(`Search: "${filters.search}"`);
    if (filters.fromDate) activeFilters.push(`From: ${filters.fromDate}`);
    if (filters.toDate) activeFilters.push(`To: ${filters.toDate}`);
    if (filters.status && filters.status !== 'all') activeFilters.push(`Status: ${filters.status}`);
    if (filters.limit && filters.limit !== 50) activeFilters.push(`Limit: ${filters.limit}`);
    
    return activeFilters.length > 0 ? activeFilters.join(', ') : 'No filters applied';
  };

  // Helper function to parse source_to_target language pair
  const parseLanguagePair = (sourceToTarget) => {
    if (!sourceToTarget) return { source: 'Unknown', target: 'Unknown' };
    
    const parts = sourceToTarget.split('_');
    if (parts.length >= 2) {
      const languageMap = {
        'en': 'English',
        'ta': 'Tamil',
        'si': 'Sinhala',
        'fr': 'French',
        'de': 'German',
        'es': 'Spanish',
        'it': 'Italian',
        'pt': 'Portuguese',
        'ru': 'Russian',
        'ja': 'Japanese',
        'ko': 'Korean',
        'zh': 'Chinese',
        'ar': 'Arabic',
        'hi': 'Hindi'
      };
      
      return {
        source: languageMap[parts[0]] || parts[0].toUpperCase(),
        target: languageMap[parts[1]] || parts[1].toUpperCase()
      };
    }
    
    return { source: 'Unknown', target: 'Unknown' };
  };

  // Helper function to get display text (truncated)
  const getDisplayText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-red-500 mb-3 sm:mb-4">
            <FaTimes className="text-3xl sm:text-4xl mx-auto mb-2" />
            <p className="text-base sm:text-lg font-semibold">Error Loading Logs</p>
          </div>
          <p className="text-pl_txt_light_3 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>
          <button
            onClick={() => loadLogs(1, true)}
            className="px-3 sm:px-4 py-2 bg-pl_prim_color_1 text-white rounded-lg hover:bg-pl_prim_color_2 transition-colors text-sm sm:text-base"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-pl_border_light_1 bg-pl_bg_light_3">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-pl_txt_light_1 flex items-center gap-2">
              <FaLanguage className="text-pl_prim_color_1 text-sm sm:text-base" />
              Translation Logs
              {totalLogs > 0 && (
                <span className="text-xs sm:text-sm text-pl_txt_light_3 font-normal">
                  ({totalLogs.toLocaleString()})
                </span>
              )}
            </h2>
            <p className="text-xs sm:text-sm text-pl_txt_light_2 truncate mt-1">
              {getFilterSummary()}
            </p>
          </div>
          <button
            onClick={() => setShowFilter(!showFilter)}
            className={`p-1.5 sm:p-2 rounded-lg transition-all flex-shrink-0 ${
              (filters.fromDate || filters.toDate || (filters.status && filters.status !== 'all') || (filters.limit && filters.limit !== 50))
                ? 'text-pl_prim_color_1 bg-pl_bg_light_5'
                : 'text-pl_txt_light_2 hover:text-pl_prim_color_1 hover:bg-pl_bg_light_5'
            }`}
          >
            <FaFilter className="text-sm sm:text-base" />
          </button>
        </div>
        
        {/* Search Bar
        <div className="relative">
          <FaSearch className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-pl_txt_light_3 text-sm sm:text-base" />
          <input
            type="text"
            placeholder="Search translations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-pl_border_light_1 rounded-lg bg-pl_bg_light_5 text-pl_txt_light_1 placeholder-pl_txt_light_3 focus:outline-none focus:ring-2 focus:ring-pl_prim_color_1 focus:border-transparent text-sm sm:text-base"
          />
        </div> */}
      </div>

      {/* Filter Panel */}
      {showFilter && (
        <div className="border-b border-pl_border_light_1">
          <FilterPanel
            filters={filters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            isMobile={false}
            isVisible={showFilter}
            onClose={() => setShowFilter(false)}
          />
        </div>
      )}

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto">
        {loading && logs.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-pl_prim_color_1"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-3 sm:p-4 text-center text-pl_txt_light_3 text-sm sm:text-base">
            {filters.search ? 'No translation logs match your search' : 'No translation logs found'}
          </div>
        ) : (
          <>
            {/* Search Results Counter */}
            {filters.search && filters.search.trim() !== '' && (
              <div className="p-3 sm:p-4 bg-pl_bg_light_5 border-b border-pl_border_light_1">
                <div className="text-sm text-pl_txt_light_1">
                  <span className="font-semibold text-pl_prim_color_1">
                    {filteredLogs.length} result{filteredLogs.length !== 1 ? 's' : ''}
                  </span>
                  {' '}found for "{filters.search}"
                </div>
              </div>
            )}
            
            <div className="divide-y divide-pl_border_light_1">
              {filteredLogs.map((log) => {
                const languagePair = parseLanguagePair(log.source_to_target);
                const logId = log._id || log.id;
                
                return (
                  <div
                    key={logId}
                    onClick={() => handleLogClick(log)}
                    className={`p-3 sm:p-4 cursor-pointer transition-all hover:bg-pl_bg_light_5 ${
                      selectedLogId === logId ? 'bg-pl_bg_light_5 border-l-4 border-l-pl_prim_color_1' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                        <span className="text-xs sm:text-sm font-medium text-pl_txt_light_1">
                          {languagePair.source} → {languagePair.target}
                        </span>
                        {log.source_type && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs rounded-full bg-blue-100 text-blue-600 self-start sm:self-auto">
                            {log.source_type}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-pl_txt_light_3">
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-xs sm:text-sm text-pl_txt_light_2 line-clamp-2">
                        <strong>Original:</strong> {getDisplayTextWithHighlight(log.input_text || log.originalText || log.original_text || log.text)}
                      </p>
                    </div>
                    
                    <div className="mb-2">
                      <p className="text-xs sm:text-sm text-pl_txt_light_2 line-clamp-2">
                        <strong>Translated:</strong> {getDisplayTextWithHighlight(log.translated_text || log.translatedText || log.translation)}
                      </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                      <span className="text-xs text-pl_txt_light_3">
                        ID: {logId.toString().substring(0, 8)}...
                      </span>
                      {log.source_type && (
                        <span className="text-xs text-pl_txt_light_3">
                          Source: {log.source_type}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Load More Button */}
              {hasMore && (
                <div className="p-3 sm:p-4 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="px-3 sm:px-4 py-2 bg-pl_prim_color_1 text-white rounded-lg hover:bg-pl_prim_color_2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
              {/* Debug info - remove in production */}
              <div className="p-2 text-xs text-pl_txt_light_3 text-center">
                Debug: hasMore={hasMore.toString()}, logs={logs.length}, filtered={filteredLogs.length}, loading={loading.toString()}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TranslateLogList; 